/**
 * Layout Component
 * Provides consistent page structure with navbar, content area, and footer
 */

import { ReactNode, useEffect, useState } from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { NotificationBanner } from '../common/NotificationBanner';
import { notificationService } from '../../services/api/NotificationService';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

interface LayoutProps {
  children: ReactNode;
}

/**
 * Layout component
 * Wraps page content with consistent navigation and footer
 * Provides the main structure for all pages in the application
 * 
 * @param props - Component props
 * @param props.children - Page content to render between navbar and footer
 * @returns Layout component with navbar, content, and footer
 */
export function Layout({ children }: LayoutProps) {
  const { isMobile } = useResponsiveLayout();
  const [notification, setNotification] = useState<{ enabled: boolean; message: string }>({ enabled: false, message: '' });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    notificationService.getNotification()
      .then((config) => {
        setNotification({ enabled: config.enabled, message: config.message });
        // If the message changed from what was dismissed, show it again
        const dismissedMessage = sessionStorage.getItem('dismissed-notification');
        setDismissed(dismissedMessage === config.message);
      })
      .catch(() => {
        // Silently fail — no banner shown, no error to user
      });
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('dismissed-notification', notification.message);
    } catch {
      // sessionStorage unavailable — graceful degradation
    }
  };

  const showBanner = notification.enabled && notification.message && !dismissed;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--color-bg)'
    }}>
      <Navbar />
      {showBanner && <NotificationBanner message={notification.message} onDismiss={handleDismiss} />}
      <main style={{
        flex: '1',
        width: '100%',
        maxWidth: '1800px',
        margin: '0 auto',
        padding: isMobile ? '0 0.25rem' : '0 1rem'
      }}>
        {children}
      </main>
      <Footer />
    </div>
  );
}
